// Copyright (c) StrangeBee 2023

import useLocalStorage, { LocalStorageKeys } from '@Hooks/useLocalStorage';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';

interface StorageTabsType {
    opened: string[];
}

interface UseStorageTabsOutput {
    tabs: StorageTabsType;
    setTabs: React.Dispatch<React.SetStateAction<StorageTabsType>>;
}

export const useStorageTabs = (): UseStorageTabsOutput => {
    const { storage, setStorage } = useLocalStorage<StorageTabsType>({
        key: LocalStorageKeys.TABS,
        initialValue: { opened: [] },
    });

    const [tabs, setTabs] = useState<StorageTabsType>(storage);

    useEffect(() => {
        setStorage((state) => ({ ...state, ...tabs }));
    }, [tabs, setStorage]);

    return { tabs, setTabs };
};

interface TabProviderProps {
    children?: ReactNode;
}
export interface TabContextInterface {
    remove: (path: string) => void;
    add: (path: string) => void;
    isOpenned: (ref: string) => boolean;
    opened: string[];
}

export const TabContext = React.createContext<TabContextInterface>({
    remove: () => {
        //empty
    },
    isOpenned: () => {
        return true;
    },
    add: () => {
        //empty
    },
    opened: [],
});
export const TabProvider = ({ children }: TabProviderProps): ReactElement => {
    const {
        tabs: { opened },
        setTabs,
    } = useStorageTabs();

    const isOpenned = (pathRef: string) => opened.includes(pathRef);

    const remove = (pathRef: string) => {
        setTabs((st) => {
            const cleanRoute = st.opened.filter((path) => pathRef !== path);
            return {
                opened: cleanRoute,
            };
        });
    };

    const add = (pathRef: string) => {
        if (!opened.includes(pathRef)) {
            setTabs((st) => {
                return {
                    opened: [...st.opened, pathRef],
                };
            });
        }
    };

    return (
        <TabContext.Provider
            value={{
                opened,
                remove,
                add,
                isOpenned,
            }}
        >
            {children}
        </TabContext.Provider>
    );
};
